import {SetSessionSelectionRequestPayload} from '@/api/api';
import {useIsV3SessionViewEnabled} from '@/api/MLSChallenge/useIsV3SessionViewEnabled';
import {useGetAthleteInfo} from '@/api/Trainer/useGetAthleteInfo';
import {useGetPlayerLastSessionSummary} from '@/api/Trainer/useGetPlayerLastSessionSummary';
import {useUpdateSessionSelection} from '@/api/Trainer/useUpdateSessionSelection';
import {useProfiles} from '@/api/User/useProfiles';
import {ConnectsBaselineSessionData} from '@/components/Connects/BaselineSessionData/ConnectsBaselineSessionData';
import {Button} from '@/components/Connects/Button';
import {ButtonTray} from '@/components/Connects/ButtonTray';
import {LogoBanner} from '@/components/Connects/LogoBanner';
import {PageHeading} from '@/components/Connects/PageHeading';
import {PlayerAssessmentConnectsBaselineSessionData} from '@/components/Connects/PlayerAssessmentBaselineSessionData/PlayerAssessmentConnectsBaselineSessionData';
import {LoadingAnimationFullHeight} from '@/components/Loader/LoadingAnimation';
import {ProfileBar} from '@/components/ProfileBar/ProfileBar';
import moment from 'moment';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';

export const ConnectsBaselineData = () => {
  const navigate = useNavigate();
  const {currentProfile} = useProfiles();
  const {trainerID} = useParams();
  const [searchParams] = useSearchParams();
  const [clientId, siteId] = [searchParams.get('mboClientId'), searchParams.get('mboSiteId')];
  const [selectedSessionIndexes, setSelectedSessionsIndexes] = useState<number[]>([]);
  const isV3SessionEnabled = useIsV3SessionViewEnabled();

  const {data: athleteInfo, isLoading} = useGetAthleteInfo({clientId, siteId});

  //Currently using this only for player assessment session, TODO: Need to refactor to use the same hook for both
  const {data: playerLastSessionSummary, isLoading: isLoadingPlayerLastSessionSummary} = useGetPlayerLastSessionSummary(
    {clientId, siteId}
  );

  const isSessionSelectionCompleted = useMemo(
    () =>
      playerLastSessionSummary?.exerciseSummary?.find((exercise) => {
        return 'confirmedBy' in exercise;
      }),
    [playerLastSessionSummary]
  );

  useEffect(() => {
    if (!isSessionSelectionCompleted && playerLastSessionSummary?.exerciseSummary) {
      const defaultSelectedIndexes: number[] = [];
      for (let i = 0; i < playerLastSessionSummary.exerciseSummary.length; i++) {
        const exercise = playerLastSessionSummary.exerciseSummary[i];
        if (exercise.defaultSelected) {
          defaultSelectedIndexes.push(i);
        }
      }
      setSelectedSessionsIndexes(defaultSelectedIndexes);
    } else {
      const selectedExerciseIndexes =
        playerLastSessionSummary?.exerciseSummary
          ?.filter((exercise) => {
            return 'confirmedBy' in exercise;
          })
          ?.map((_, index) => index) || [];
      setSelectedSessionsIndexes(selectedExerciseIndexes);
    }
  }, [isSessionSelectionCompleted, playerLastSessionSummary]);

  const startDate = playerLastSessionSummary?.startTime
    ? moment(Number(playerLastSessionSummary.startTime)).format('MMM DD, YYYY')
    : undefined;

  const {setSessionSelection} = useUpdateSessionSelection({clientId});

  const toggleSessionSelection = useCallback(
    (sessionIndex: number) => {
      if (!selectedSessionIndexes.includes(sessionIndex)) {
        setSelectedSessionsIndexes((prevSelection) => [...prevSelection, sessionIndex]);
      } else {
        setSelectedSessionsIndexes((prevSelection) =>
          prevSelection.filter((selectedIndex) => selectedIndex !== sessionIndex)
        );
      }
    },
    [setSelectedSessionsIndexes, selectedSessionIndexes]
  );

  const confirmSessionSelectionHandler = () => {
    // TODO It might be better to always use the profile `_id` instead of the optional `mboDetails.clientId`
    const trainerId = currentProfile?.mboDetails?.clientId || currentProfile._id;

    const requestPayload: SetSessionSelectionRequestPayload = {
      clientSessionSummaryId: playerLastSessionSummary?.clientSessionSummaryId || '',
      trainerId: trainerId || '',
      selectedExercisesIndex: selectedSessionIndexes,
    };
    setSessionSelection(requestPayload);
  };

  const onClickContinueHandler = () => {
    navigate(`/trainer/connects/schedule?trainer=${trainerID}&mboClientId=${clientId}&mboSiteId=${siteId}`);
  };

  const isPlayerAssessmentSession = playerLastSessionSummary?.isPlayerAssessment;
  const heading =
    isPlayerAssessmentSession && !isSessionSelectionCompleted
      ? 'Review and Approve Player Data'
      : 'Baseline Session Data';

  useEffect(() => {
    if (isPlayerAssessmentSession) {
      return;
    }

    if (isLoading || isLoadingPlayerLastSessionSummary) {
      return;
    }

    // Navigate if V3 session is enabled or if it's not a baseline session
    if (isV3SessionEnabled || !athleteInfo?.lastSession?.isBaseline) {
      navigate(
        `/trainer/connects?trainer=${trainerID}&mboClientId=${clientId}&mboSiteId=${siteId}&baselineVisited=true`,
        {replace: true}
      );
    }
  }, [
    isPlayerAssessmentSession,
    isLoading,
    isLoadingPlayerLastSessionSummary,
    isV3SessionEnabled,
    athleteInfo?.lastSession?.isBaseline,
    navigate,
    trainerID,
    clientId,
    siteId,
  ]);

  return (
    <>
      <Helmet>
        <title>MyTOCA | Connects</title>
      </Helmet>

      {isLoading || isLoadingPlayerLastSessionSummary ? (
        <LoadingAnimationFullHeight />
      ) : athleteInfo ? (
        <div className="grow flex flex-col">
          <LogoBanner />
          <ProfileBar athleteInfo={athleteInfo} />

          <div className="my-6 text-center">
            <PageHeading text={heading} />
          </div>

          {isPlayerAssessmentSession ? (
            <>
              <div className="exceed-global-padding lg:max-w-[800px] lg:mx-auto lg:w-full">
                <PlayerAssessmentConnectsBaselineSessionData
                  sessionSummary={playerLastSessionSummary}
                  toggleSessionSelection={toggleSessionSelection}
                  enableSessionSelection={!isSessionSelectionCompleted}
                  selectedSessionIndexes={selectedSessionIndexes}
                  date={startDate}
                />
              </div>
            </>
          ) : athleteInfo?.lastSession?.isBaseline && playerLastSessionSummary ? (
            <ConnectsBaselineSessionData
              startTime={playerLastSessionSummary.startTime}
              baselineLevel={playerLastSessionSummary.baselineLevel}
              totalBallsFired={playerLastSessionSummary.sessionSummary.totalBallsFired}
              repetitions={playerLastSessionSummary.sessionSummary.repetitions}
              exercises={playerLastSessionSummary.exerciseSummary}
              isV3Session={playerLastSessionSummary.isV3Session}
              sessionName={playerLastSessionSummary.sessionName}
              score={playerLastSessionSummary.sessionSummary.score || 0}
            />
          ) : (
            <div className="grid place-content-center">
              <p className="text-sm text-grey-xdark">Error loading baseline session data</p>
            </div>
          )}

          <div className="md:mx-auto md:max-w-[600px] md:w-full mt-8">
            <ButtonTray>
              <Button
                text={!isPlayerAssessmentSession || isSessionSelectionCompleted ? 'Continue' : 'Confirm Selection'}
                color="secondary"
                variant="cta"
                onClick={
                  !isPlayerAssessmentSession || isSessionSelectionCompleted
                    ? onClickContinueHandler
                    : confirmSessionSelectionHandler
                }
              />
            </ButtonTray>
          </div>
        </div>
      ) : (
        <div className="grow flex flex-col">
          <p className="text-sm text-grey-xdark text-center p-4">unable to load athlete data</p>
        </div>
      )}
    </>
  );
};
